import React, { useEffect, useState } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { staffAttApi, errMsg } from '@/services/staffAttendanceApi';
import { slug } from '@/components/staffAttendance/PayrollAgencyCard';

const RATES = [['day_rate', '₹ / day', 'agency-rate-input'], ['truck_rate', 'Truck ₹ / day', 'agency-truck-rate-input'], ['hourly_rate', 'Unload ₹ / hr', 'agency-hourly-rate-input']];
const blank = (v) => v ?? '';
const val = (v) => (v === '' ? null : Number(v));

/** HR: per-agency rates — regular day, Truck Loadman day and Unloading hourly (agency names come from Staff Master). */
export const AgencyRatesDialog = ({ open, onClose, onSaved }) => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setRows(null);
    staffAttApi.contractAgencies().then((d) => setRows(d.agencies.map((a) => ({ ...a, day_rate: blank(a.day_rate), truck_rate: blank(a.truck_rate), hourly_rate: blank(a.hourly_rate) }))))
      .catch((e) => { toast.error(errMsg(e, 'Failed to load agencies')); setRows([]); });
  }, [open]);
  const set = (i, patch) => setRows((r) => r.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const save = async () => {
    setBusy(true);
    try {
      await staffAttApi.saveContractAgencies(rows.filter((r) => r.name.trim()).map((r) => ({
        name: r.name.trim(), day_rate: val(r.day_rate), truck_rate: val(r.truck_rate), hourly_rate: val(r.hourly_rate) })));
      toast.success('Agency rates saved');
      onSaved();
    } catch (e) { toast.error(errMsg(e, 'Could not save rates')); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="agency-rates-dialog">
        <DialogHeader>
          <DialogTitle>Agency rates</DialogTitle>
          <DialogDescription>Bill = days × day rate + Truck Loadman days × truck rate + Unloading hours × hourly rate. Piece Rate staff are paid in Accounts. Leave blank to show quantities only.</DialogDescription>
        </DialogHeader>
        {!rows ? <Loader2 className="w-6 h-6 animate-spin mx-auto my-6 text-amber-600" /> : (
          <div className="space-y-2 max-h-[55vh] overflow-y-auto">
            {rows.length === 0 && <p className="text-sm text-gray-500 py-2" data-testid="agency-rates-empty">No agencies yet — add one, or set Agency on contract workers in Staff Master.</p>}
            {rows.length > 0 && (
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-gray-500">
                <span className="flex-1">Agency</span><span className="w-16" />{RATES.map(([k, l]) => <span key={k} className="w-28">{l}</span>)}<span className="w-9" />
              </div>
            )}
            {rows.map((r, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`agency-rate-row-${slug(r.name) || i}`}>
                <Input value={r.name} disabled={r.in_staff_master} onChange={(e) => set(i, { name: e.target.value })} placeholder="Agency name" className="flex-1 bg-white disabled:opacity-100 disabled:bg-slate-50 disabled:text-gray-900 disabled:font-medium" data-testid={`agency-rate-name-${i}`} />
                <span className="text-xs text-gray-500 w-16 text-right">{r.in_staff_master ? `${r.workers} staff` : 'new'}</span>
                {RATES.map(([k, l, tid]) => (
                  <Input key={k} type="number" min="0" step="0.01" value={r[k]} onChange={(e) => set(i, { [k]: e.target.value })} placeholder={l} className="w-28 bg-white" data-testid={`${tid}-${i}`} />
                ))}
                {r.in_staff_master ? <span className="w-9" /> : <Button size="icon" variant="ghost" onClick={() => setRows(rows.filter((_, j) => j !== i))} data-testid={`agency-rate-remove-${i}`}><Trash2 className="w-4 h-4" /></Button>}
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setRows([...rows, { name: '', day_rate: '', truck_rate: '', hourly_rate: '', in_staff_master: false, workers: 0 }])} data-testid="agency-rate-add"><Plus className="w-4 h-4 mr-1" />Add agency</Button>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="agency-rates-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy || !rows} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="agency-rates-save">{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Save rates</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AgencyRatesDialog;
